import * as React from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  IonContent,
  IonPage,
  useIonViewWillLeave,
  useIonViewWillEnter,
} from "@ionic/react";

import BoxDetail from "../../../templates/teacher/BoxDetail";
import EmbeddedHeader from "../../../organisms/teacher/Header/EmbeddedHeader";
import EmbeddedHeaderModal from "../../../organisms/EmbeddedHeaderModal";
import { IOnClickStudentAvatarParams } from "../../../state";
import {
  Context,
  fetchGearboxRealizations,
  clearRealizations,
  clearSelectedGearbox,
  clearPagyInfo,
  sharedRealizationListPath,
  clearSuccessMessage,
  CREATE_FEEDBACK_SUCCESS_MESSAGE,
  UPDATE_FEEDBACK_SUCCESS_MESSAGE,
  DELETE_FEEDBACK_SUCCESS_MESSAGE,
  fetchFeedbacks,
  CREATE_COMMENT_SUCCESS_MESSAGE,
  UPDATE_COMMENT_SUCCESS_MESSAGE,
  DELETE_COMMENT_SUCCESS_MESSAGE,
  fetchComments,
  createComment,
  updateComment,
  deleteComment,
  clearComments,
  clearFeedbacks,
  CREATE_REALIZATION_STAMP_REACTION_SUCCESS,
  createRealizationStampReaction,
  fetchRealizationConversations,
  clearConversations,
  Actions,
  addFavorite,
  removeFavorite,
} from "../../../store/teacher";
import styles from "../common.module.scss";
import useWaitApiCall from "../../../hooks/useWaitApiCall";
import SharedRealization from "../../../templates/teacher/SharedRealization";
import type { Stamp } from "../../../libs/stamp";

const BoxDetailPage = () => {
  const { dispatch, contextState } = React.useContext(Context);
  const [calling, peep] = useWaitApiCall(contextState);
  const [values, updateValues] = React.useState({
    realizationDetailId: 0,
    isDisplaying: false,
  });
  const params = useParams<{ id: string }>();
  const history = useHistory();

  const update = (args: { [key: string]: any }) => {
    updateValues(prevValues => ({ ...prevValues, ...args }));
  };

  const contentRef = React.useRef<HTMLIonContentElement>(null);
  const fetchContents = React.useCallback(
    (realization_id: number, fetchFunctions: ((id: number) => Actions)[]) => {
      dispatch(clearSuccessMessage());
      fetchFunctions.forEach(
        func => realization_id !== 0 && dispatch(func(realization_id)),
      );
      setTimeout(() => {
        contentRef.current?.scrollToBottom(500);
      }, 500);
    },
    [dispatch, contentRef],
  );

  const fetchGearboxRealizationsFunc = React.useCallback(
    (page?: number) => {
      dispatch(clearSuccessMessage());
      dispatch(peep(fetchGearboxRealizations(Number(params.id), page ?? 1)));
    },
    [dispatch, params.id, peep],
  );

  useIonViewWillEnter(() => {
    update({
      isDisplaying: true,
    });
    dispatch(peep(fetchGearboxRealizations(Number(params.id), 1)));
  }, [params.id]);

  useIonViewWillLeave(() => {
    update({
      isDisplaying: false,
    });
    dispatch(clearRealizations());
    dispatch(clearSelectedGearbox());
    dispatch(clearPagyInfo());
    clearRealizationFunc();
  });

  const realizationDetail = React.useMemo(
    () =>
      contextState.realizations.find(
        realization => realization.id === values.realizationDetailId,
      ),
    [contextState.realizations, values.realizationDetailId],
  );

  React.useEffect(() => {
    if (
      contextState.success_message === CREATE_FEEDBACK_SUCCESS_MESSAGE ||
      contextState.success_message === UPDATE_FEEDBACK_SUCCESS_MESSAGE ||
      contextState.success_message === DELETE_FEEDBACK_SUCCESS_MESSAGE
    ) {
      fetchContents(values.realizationDetailId, [fetchFeedbacks]);
    }
    if (
      contextState.success_message === CREATE_COMMENT_SUCCESS_MESSAGE ||
      contextState.success_message === UPDATE_COMMENT_SUCCESS_MESSAGE ||
      contextState.success_message === DELETE_COMMENT_SUCCESS_MESSAGE
    ) {
      fetchContents(values.realizationDetailId, [fetchComments]);
    }
    if (
      contextState.success_message === CREATE_REALIZATION_STAMP_REACTION_SUCCESS
    ) {
      fetchContents(values.realizationDetailId, [
        fetchComments,
        fetchFeedbacks,
      ]);
    }
  }, [
    dispatch,
    contextState.success_message,
    values.realizationDetailId,
    fetchContents,
  ]);

  const clearRealizationFunc = React.useCallback(() => {
    update({ realizationDetailId: 0 });
    dispatch(clearComments());
    dispatch(clearFeedbacks());
    dispatch(clearConversations());
  }, [dispatch]);

  React.useEffect(() => {
    if (values.realizationDetailId !== 0 && values.isDisplaying) {
      history.location.pathname.includes(sharedRealizationListPath)
        ? dispatch(fetchComments(values.realizationDetailId))
        : dispatch(fetchFeedbacks(values.realizationDetailId));
      dispatch(fetchRealizationConversations(values.realizationDetailId));
    }
  }, [
    dispatch,
    values.realizationDetailId,
    history.location.pathname,
    values.isDisplaying,
  ]);

  const createCommentFunc = React.useCallback(
    (realization_id: number, content: string) => {
      dispatch(peep(createComment(realization_id, content)));
    },
    [dispatch, peep],
  );

  const updateCommentFunc = React.useCallback(
    (comment_id: number, content: string) => {
      dispatch(peep(updateComment(comment_id, content)));
    },
    [dispatch, peep],
  );

  const deleteCommentFunc = React.useCallback(
    (comment_id: number) => {
      dispatch(peep(deleteComment(comment_id)));
    },
    [dispatch, peep],
  );

  const createStampReactionFunc = (realization_id: number, stamp: Stamp) => {
    dispatch(
      peep(
        createRealizationStampReaction(realization_id, {
          type: "comment",
          realization_stamp_id: stamp.id,
        }),
      ),
    );
  };

  const showStudentDetailFunc = ({
    class_id,
    student_id,
  }: IOnClickStudentAvatarParams) => {
    window.location.href = `${process.env.REACT_APP_TEACHER_ONE_MENU_URL}/students/${class_id}/${student_id}`;
  };

  const addFavoriteFunc = React.useCallback(
    (id: number) => {
      if (calling) return;
      dispatch(peep(addFavorite(id)));
    },
    [dispatch, peep, calling],
  );

  const removeFavoriteFunc = React.useCallback(
    (id: number) => {
      if (calling) return;
      dispatch(peep(removeFavorite(id)));
    },
    [dispatch, peep, calling],
  );

  return (
    <IonPage>
      {realizationDetail && (
        <EmbeddedHeaderModal
          isOpen={!!values.realizationDetailId}
          title="詳細"
          onClose={clearRealizationFunc}
        >
          <IonContent ref={contentRef}>
            <SharedRealization
              calling={calling}
              realization={realizationDetail}
              comments={contextState.comments}
              conversations={contextState.conversations}
              current_id={contextState.teacher.id}
              createComment={createCommentFunc}
              updateComment={updateCommentFunc}
              deleteComment={deleteCommentFunc}
              createStampReaction={createStampReactionFunc}
              onAvatarClick={showStudentDetailFunc}
              addFavorite={addFavoriteFunc}
              removeFavorite={removeFavoriteFunc}
            />
          </IonContent>
        </EmbeddedHeaderModal>
      )}
      <IonContent className={`${styles.wrapper} ${styles.bgWhileBase}`}>
        <div className={styles.container}>
          <EmbeddedHeader title="BOX詳細" />
          <BoxDetail
            isDisplaying={values.isDisplaying}
            calling={calling}
            gearbox={contextState.selected_gearbox}
            realizations={contextState.realizations}
            pagy_info={contextState.pagy_info}
            fetchRealizations={fetchGearboxRealizationsFunc}
            onAction={realizationDetailId => {
              update({ realizationDetailId });
            }}
            showStudentDetail={showStudentDetailFunc}
            addFavorite={addFavoriteFunc}
            removeFavorite={removeFavoriteFunc}
          />
        </div>
      </IonContent>
    </IonPage>
  );
};

export default BoxDetailPage;
